import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { lightThemeColors } from "../../assets/styles/colors";
import ConfigurationSectionHeader from "../configurationComponents/ConfigurationSectionHeader";
import KeyPairValues from "./KeyPairValues";
import SecondaryButton from "../Generic/SecondaryButton";
import { ReactComponent as PlusSvg } from "../../assets/icons/generic/plus.svg";
import cloneDeep from "clone-deep";
import { useExpanded } from "../../hooks/useExpanded";
import SubcategoryTitleWrapper from "../configurationComponents/SubcategoryTitleWrapper";
import { ReactComponent as AndroidIcon } from "../../assets/icons/generic/AndroidIconSmall.svg";
import OptionSelectComponent from "../Generic/OptionSelectComponent";

import InputFieldWithValidation from "../Generic/InputFieldWithValidation";
import { isCustomUrlValid } from "../../helper/ValidatorsHelper";
import { ReactComponent as AppleIcon } from "../../assets/icons/generic/AppleIconSmall.svg";
import { ReactComponent as ComputerIcon } from "../../assets/icons/generic/DesktopIconSmall.svg";
import { ReactComponent as BehaviourIcon } from "../../assets/icons/generic/BehaviourIcon.svg";
import { getRedirectType } from "../../helper/redirectsHelpers";
import {
  APP_OR_FALLBACK,
  DEFAULT,
  CUSTOM,
  AUTOMATIC,
  SHOW_PREVIEWS,
} from "../../constants/OptionsConstants";
import { Title } from "@mui/icons-material";

const RedirectsLink = ({
  androidRedirectURL,
  setAndroidRedirectURL,
  iOSRedirectURL,
  setiOSRedirectURL,
  desktopRedirectURL,
  setDesktopRedirectURL,
  disabledActions,
  noLabel,
  showPreviewAndroid,
  setShowPreviewAndroid,
  showPreviewIOS,
  setShowPreviewIOS,
}) => {
  const [expanded, expand] = useExpanded(false);

  const [androidRedirectType, setAndroidRedirectType] = useState(
    getRedirectType(androidRedirectURL)
  );

  const [iOSRedirectType, setiOSRedirectType] = useState(
    getRedirectType(iOSRedirectURL)
  );
  const [desktopRedirectType, setDesktopRedirectType] = useState(
    getRedirectType(desktopRedirectURL)
  );

  const [androidLinkBehaviour, setAndroidLinkBehaviour] = useState(() => {
    if (showPreviewAndroid === undefined || showPreviewAndroid === null) {
      return DEFAULT;
    }
    return showPreviewAndroid ? SHOW_PREVIEWS : AUTOMATIC;
  });

  const [iosLinkBehaviour, setIosLinkBehaviour] = useState(() => {
    if (showPreviewIOS === undefined || showPreviewIOS === null) {
      return DEFAULT;
    }
    return showPreviewIOS ? SHOW_PREVIEWS : AUTOMATIC;
  });

  const OPTIONS_LIST = [
    {
      text: "Default Redirect",
      value: DEFAULT,
    },
    {
      text: "Web URL",
      value: CUSTOM,
    },
    {
      text: "App or Web Fallback",
      value: APP_OR_FALLBACK,
    },
  ];

  const OPTIONS_LIST_DESKTOP = [
    {
      text: "Default Redirect",
      value: DEFAULT,
    },
    {
      text: "Web URL",
      value: CUSTOM,
    },
  ];

  const BEHAVIOUR_OPTIONS = [
    {
      text: "Default",
      value: DEFAULT,
    },
    {
      text: "Show",
      value: SHOW_PREVIEWS,
    },
    {
      text: "Skip",
      value: AUTOMATIC,
    },
  ];

  useEffect(() => {
    if (androidRedirectType === DEFAULT) {
      setAndroidRedirectURL(null);
      return;
    }

    setAndroidRedirectURL((prev) => ({
      url: prev?.url ?? "",
      open_app_if_installed: androidRedirectType === APP_OR_FALLBACK,
    }));
  }, [androidRedirectType]);

  useEffect(() => {
    if (iOSRedirectType === DEFAULT) {
      setiOSRedirectURL(null);
      return;
    }

    setiOSRedirectURL((prev) => ({
      url: prev?.url ?? "",
      open_app_if_installed: iOSRedirectType === APP_OR_FALLBACK,
    }));
  }, [iOSRedirectType]);

  useEffect(() => {
    if (desktopRedirectType === DEFAULT) {
      setDesktopRedirectURL(null);
    }
  }, [desktopRedirectType]);

  return (
    <Container>
      <ConfigurationSectionHeader
        title={"Redirects"}
        handleExpand={expand}
        expanded={expanded}
        optionalLabel={!noLabel}
      />

      {expanded && (
        <ContentContainer>
          <SubcategoryContent>
            {/* Android */}
            <ItemContainer>
              <RowContainer>
                <SubcategoryTitleWrapper
                  icon={<AndroidIcon />}
                  text="Android"
                  id={"AndroidConfigId"}
                  colorType={"faded-dark"}
                />
                <OptionSelectComponent
                  optionsList={OPTIONS_LIST}
                  selectedOption={androidRedirectType}
                  setSelectedOption={setAndroidRedirectType}
                  disabled={disabledActions}
                />
              </RowContainer>
              {androidRedirectType === CUSTOM && (
                <ItemContainer>
                  <InputFieldWithValidation
                    placeholder={"Enter the Android redirect URL..."}
                    inputValue={androidRedirectURL?.url}
                    handleInputChange={(e) =>
                      setAndroidRedirectURL((prev) => ({
                        ...(prev || {}),
                        url: e.target.value,
                        open_app_if_installed: false, // or true, depending
                      }))
                    }
                    valid={isCustomUrlValid(androidRedirectURL?.url)}
                    readOnly={disabledActions}
                  />
                  <InfoText>
                    This link will bypass the project's default redirection
                    rules and use the specified URL above when redirecting on
                    Android devices.
                  </InfoText>
                </ItemContainer>
              )}
              {androidRedirectType === APP_OR_FALLBACK && (
                <ItemContainer>
                  <InputFieldWithValidation
                    placeholder={
                      "Enter the Android app not installed fallback URL..."
                    }
                    inputValue={androidRedirectURL?.url}
                    handleInputChange={(e) =>
                      setAndroidRedirectURL((prev) => ({
                        ...(prev || {}),
                        url: e.target.value,
                        open_app_if_installed: true, // or true, depending
                      }))
                    }
                    valid={isCustomUrlValid(androidRedirectURL?.url)}
                    readOnly={disabledActions}
                  />
                  <InfoText>
                    If the app is not installed, users will be redirected to
                    this fallback URL on Android devices, if they have the app
                    installed, they will be redirected to the app.
                  </InfoText>
                </ItemContainer>
              )}
              {androidRedirectType === DEFAULT && (
                <ItemContainer>
                  <InfoText>
                    The default redirection settings for this project will be
                    used on Android devices.
                  </InfoText>
                </ItemContainer>
              )}
              <RowContainer>
                <SubcategoryTitleWrapper
                  text="Preview page"
                  colorType={"faded-dark"}
                  small={false}
                />
                <OptionSelectComponent
                  optionsList={BEHAVIOUR_OPTIONS}
                  selectedOption={androidLinkBehaviour}
                  setSelectedOption={(value) => {
                    setAndroidLinkBehaviour(value);

                    const mappedValue =
                      value === "show_previews"
                        ? true
                        : value === "automatic"
                        ? false
                        : null;

                    setShowPreviewAndroid(mappedValue);
                  }}
                  disabled={disabledActions}
                />
              </RowContainer>
              {androidLinkBehaviour === DEFAULT && (
                <ItemContainer>
                  <InfoText>
                    The default behaviour settings for this project will be used
                    on this link.
                  </InfoText>
                </ItemContainer>
              )}

              {androidLinkBehaviour === SHOW_PREVIEWS && (
                <ItemContainer>
                  <InfoText>
                    Displays a preview before redirecting to the app or store.
                  </InfoText>
                </ItemContainer>
              )}

              {androidLinkBehaviour === AUTOMATIC && (
                <ItemContainer>
                  <InfoText>
                    Skips the preview and tries to open the app or store
                    directly. May be less accurate, but faster—ideal for guiding
                    users to install apps with fewer steps.
                  </InfoText>
                </ItemContainer>
              )}
            </ItemContainer>
            {/* iOS  */}
            <ItemContainer>
              <RowContainer>
                <SubcategoryTitleWrapper
                  icon={<AppleIcon />}
                  text="iOS"
                  colorType={"faded-dark"}
                />
                <OptionSelectComponent
                  optionsList={OPTIONS_LIST}
                  selectedOption={iOSRedirectType}
                  setSelectedOption={setiOSRedirectType}
                  disabled={disabledActions}
                />
              </RowContainer>
              {iOSRedirectType === CUSTOM && (
                <ItemContainer>
                  <InputFieldWithValidation
                    placeholder={"Enter the iOS redirect URL..."}
                    inputValue={iOSRedirectURL?.url}
                    handleInputChange={(e) =>
                      setiOSRedirectURL((prev) => ({
                        ...(prev || {}),
                        url: e.target.value,
                        open_app_if_installed: false, // or true, depending
                      }))
                    }
                    valid={isCustomUrlValid(iOSRedirectURL?.url)}
                    readOnly={disabledActions}
                  />
                  <InfoText>
                    This link will bypass the project's default redirection
                    rules and use the specified URL above when redirecting on
                    iOS devices.
                  </InfoText>
                </ItemContainer>
              )}
              {iOSRedirectType === APP_OR_FALLBACK && (
                <ItemContainer>
                  <InputFieldWithValidation
                    placeholder={
                      "Enter the iOS app not installed fallback URL..."
                    }
                    inputValue={iOSRedirectURL?.url}
                    handleInputChange={(e) =>
                      setiOSRedirectURL((prev) => ({
                        ...(prev || {}),
                        url: e.target.value,
                        open_app_if_installed: true, // or true, depending
                      }))
                    }
                    valid={isCustomUrlValid(iOSRedirectURL?.url)}
                    readOnly={disabledActions}
                  />
                  <InfoText>
                    If the app is not installed, users will be redirected to
                    this fallback URL on iOS devices, if they have the app
                    installed, they will be redirected to the app.
                  </InfoText>
                </ItemContainer>
              )}
              {iOSRedirectType === DEFAULT && (
                <ItemContainer>
                  <InfoText>
                    The default redirection settings for this project will be
                    used on iOS devices.
                  </InfoText>
                </ItemContainer>
              )}
              <RowContainer>
                <SubcategoryTitleWrapper
                  text="Preview page"
                  colorType={"faded-dark"}
                  small={false}
                />
                <OptionSelectComponent
                  optionsList={BEHAVIOUR_OPTIONS}
                  selectedOption={iosLinkBehaviour}
                  setSelectedOption={(value) => {
                    setIosLinkBehaviour(value);

                    const mappedValue =
                      value === "show_previews"
                        ? true
                        : value === "automatic"
                        ? false
                        : null;

                    setShowPreviewIOS(mappedValue);
                  }}
                  disabled={disabledActions}
                />
              </RowContainer>
              {iosLinkBehaviour === DEFAULT && (
                <ItemContainer>
                  <InfoText>
                    The default behaviour settings for this project will be used
                    on this link.
                  </InfoText>
                </ItemContainer>
              )}

              {iosLinkBehaviour === SHOW_PREVIEWS && (
                <ItemContainer>
                  <InfoText>
                    Displays a preview before redirecting to the app or store.
                  </InfoText>
                </ItemContainer>
              )}

              {iosLinkBehaviour === AUTOMATIC && (
                <ItemContainer>
                  <InfoText>
                    Skips the preview and tries to open the app or store
                    directly. May be less accurate, but faster—ideal for guiding
                    users to install apps with fewer steps.
                  </InfoText>
                </ItemContainer>
              )}
            </ItemContainer>
            {/* Computers  */}
            <ItemContainer>
              <RowContainer>
                <SubcategoryTitleWrapper
                  icon={<ComputerIcon />}
                  text="Computers"
                  colorType={"faded-dark"}
                />
                <OptionSelectComponent
                  optionsList={OPTIONS_LIST_DESKTOP}
                  selectedOption={desktopRedirectType}
                  setSelectedOption={setDesktopRedirectType}
                  disabled={disabledActions}
                />
              </RowContainer>
              {desktopRedirectType === CUSTOM ? (
                <ItemContainer>
                  <InputFieldWithValidation
                    placeholder={"Enter the computers redirect URL..."}
                    inputValue={desktopRedirectURL?.url}
                    handleInputChange={(e) =>
                      setDesktopRedirectURL({
                        url: e.currentTarget.value,
                        open_app_if_installed: false,
                      })
                    }
                    valid={isCustomUrlValid(desktopRedirectURL?.url)}
                    readOnly={disabledActions}
                  />
                  <InfoText>
                    This link will bypass the project's default redirection
                    rules and use the specified URL above when redirecting on
                    non-mobile devices (such as desktops and laptops).
                  </InfoText>
                </ItemContainer>
              ) : (
                <ItemContainer>
                  <InfoText>
                    The default redirection settings for this project will be
                    used on non-mobile devices.
                  </InfoText>
                </ItemContainer>
              )}
            </ItemContainer>
          </SubcategoryContent>
        </ContentContainer>
      )}
    </Container>
  );
};

export default RedirectsLink;

const RowContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  gap: 20px;
`;

const ItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: ${lightThemeColors.background};
  width: 100%;
  gap: 20px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background: ${lightThemeColors.background};
  width: 100%;
  gap: 30px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding-bottom: 20px;
  row-gap: 20px;
  height: 100%;
`;
const SubcategoryContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  width: 100%;
  padding: 0 1px;
`;

const InfoText = styled.p`
  color: ${lightThemeColors.inputSubtitleColor};
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-align: left;
`;

const Subtitle = styled.p`
  color: ${lightThemeColors.darkColor};
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  text-align: left;
`;
